import { Pencil } from "lucide-react";

import type { LeaveBalanceMatrixResponse } from "../types";
import type { AdjustBalanceTarget } from "./AdjustBalanceModal";

type Props = {
  data: LeaveBalanceMatrixResponse;
  /** Omit to hide the per-cell "Adjust" action entirely (e.g. for view-only users). */
  onAdjust?: (target: AdjustBalanceTarget) => void;
};

export function LeaveBalancesTable({ data, onAdjust }: Props) {
  const columns = data.columns || [];
  const rows = data.rows || [];

  return (
    <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
      <div className="border-b bg-white px-5 py-4">
        <h2 className="text-base font-black text-slate-900">
          Employee Leave Balances - {data.leaveYear}
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Shows entitlement, pending days, used days, and remaining balance for
          each employee.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="sticky left-0 z-20 min-w-64 bg-slate-50 px-4 py-3">
                Employee
              </th>

              <th className="min-w-36 px-4 py-3">Gender</th>
              <th className="min-w-40 px-4 py-3">Department</th>

              {columns.map((type) => (
                <th key={type.leaveTypeId} className="min-w-56 px-4 py-3">
                  <div>{type.leaveTypeName}</div>
                  <div className="font-normal text-slate-400">
                    {type.leaveTypeCode}
                  </div>
                  {type.applicableGender && type.applicableGender !== "ALL" ? (
                    <div className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-bold ${
                      type.applicableGender === "FEMALE"
                        ? "bg-pink-100 text-pink-700"
                        : "bg-blue-100 text-blue-700"
                    }`}>
                      {type.applicableGender === "FEMALE" ? "♀ Female only" : "♂ Male only"}
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y">
            {rows.map((employee) => (
              <tr key={employee.employeeId} className="hover:bg-slate-50">
                <td className="sticky left-0 z-10 bg-white px-4 py-3">
                  <div className="font-black text-slate-900">
                    {employee.employeeName}
                  </div>

                  <div className="mt-1 text-xs text-slate-400">
                    {employee.employeeNumber}
                  </div>

                  {employee.jobTitle ? (
                    <div className="mt-1 text-xs text-slate-400">
                      {employee.jobTitle}
                    </div>
                  ) : null}

                  {employee.departmentName ? (
                    <div className="mt-1 text-xs text-slate-400">
                      {employee.departmentName}
                    </div>
                  ) : null}
                </td>

                <td className="px-4 py-3">
                  {(() => {
                    const display = employee.genderName || employee.genderCode || employee.gender;
                    const code = (employee.genderCode || employee.gender || "").toUpperCase();
                    return (
                      <AttributeCell
                        value={display}
                        tone={
                          !display
                            ? "missing"
                            : code === "FEMALE"
                              ? "female"
                              : code === "MALE"
                                ? "male"
                                : "neutral"
                        }
                      />
                    );
                  })()}
                </td>

                <td className="px-4 py-3">
                  <AttributeCell
                    value={employee.departmentName}
                    tone={!employee.departmentName ? "missing" : "neutral"}
                  />
                </td>

                {columns.map((type) => {
                  const cell = employee.cells?.find(
                    (item) => item.leaveTypeId === type.leaveTypeId,
                  );

                  return (
                    <td key={type.leaveTypeId} className="px-4 py-3">
                      {!cell || !cell.applicable ? (
                        <NotApplicableCell />
                      ) : (
                        <BalanceCell
                          opening={cell.openingBalance ?? 0}
                          entitled={cell.entitledDays ?? 0}
                          earned={cell.earnedDays ?? 0}
                          broughtForward={cell.broughtForwardDays ?? 0}
                          pending={cell.pendingDays ?? 0}
                          used={cell.usedDays ?? 0}
                          forfeited={cell.forfeitedDays ?? 0}
                          remaining={cell.remainingDays ?? 0}
                          onAdjust={
                            onAdjust
                              ? () =>
                                  onAdjust({
                                    employeeId: employee.employeeId,
                                    employeeName: employee.employeeName,
                                    leaveTypeId: type.leaveTypeId,
                                    leaveTypeName: type.leaveTypeName,
                                    leaveYear: data.leaveYear,
                                    currentEntitledDays: cell.entitledDays ?? 0,
                                    currentBroughtForwardDays: cell.broughtForwardDays ?? 0,
                                    currentUsedDays: cell.usedDays ?? 0,
                                  })
                              : undefined
                          }
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}

            {!rows.length ? (
              <tr>
                <td
                  colSpan={3 + columns.length}
                  className="px-4 py-12 text-center text-slate-500"
                >
                  No employees found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BalanceCell({
  opening,
  entitled,
  earned,
  broughtForward,
  pending,
  used,
  forfeited,
  remaining,
  onAdjust,
}: {
  opening: number;
  entitled: number;
  earned: number;
  broughtForward: number;
  pending: number;
  used: number;
  forfeited: number;
  remaining: number;
  onAdjust?: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="inline-flex overflow-hidden rounded-xl border bg-white text-sm font-black shadow-sm">
          <div className="min-w-14 bg-emerald-100 px-3 py-2 text-center text-emerald-800">
            {formatDays(entitled)}
          </div>

          <div
            className={`min-w-14 px-3 py-2 text-center ${balanceClass(
              entitled,
              remaining,
            )}`}
          >
            {formatDays(remaining)}
          </div>
        </div>

        {onAdjust ? (
          <button
            type="button"
            onClick={onAdjust}
            title="Adjust entitlement / brought-forward days"
            className="rounded-lg border p-1.5 text-slate-400 hover:border-emerald-300 hover:text-emerald-700"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-500">
        <span>Opening: {formatDays(opening)}</span>
        <span>Earned: {formatDays(earned)}</span>
        <span>B/F: {formatDays(broughtForward)}</span>
        <span>Pending: {formatDays(pending)}</span>
        <span>Used: {formatDays(used)}</span>
        <span>Forfeited: {formatDays(forfeited)}</span>
      </div>
    </div>
  );
}

function NotApplicableCell() {
  return (
    <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-black text-slate-400">
      N/A
    </div>
  );
}

function balanceClass(entitled: number, remaining: number) {
  if (remaining <= 0) return "bg-red-100 text-red-800";
  if (entitled > 0 && remaining < entitled) return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

function formatDays(value?: number | null) {
  const safeValue = Number(value ?? 0);
  return Number.isInteger(safeValue) ? String(safeValue) : safeValue.toFixed(2);
}

function AttributeCell({
  value,
  tone,
}: {
  value?: string | null;
  tone: "missing" | "female" | "male" | "neutral";
}) {
  if (!value || tone === "missing") {
    return (
      <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-400 ring-1 ring-red-100">
        Not set
      </span>
    );
  }

  const display = value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());

  if (tone === "female") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2 py-0.5 text-xs font-semibold text-pink-700">
        ♀ {display}
      </span>
    );
  }

  if (tone === "male") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
        ♂ {display}
      </span>
    );
  }

  return <span className="text-sm text-slate-600">{display}</span>;
}

function formatValue(value?: string | null) {
  if (!value) return "-";

  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}